import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { Email, EmailThread } from "../utils/interfaces";
import type { ReplyMode, ReplySelfContext } from "../utils/replyRecipients";
import { EmailReplyModal } from "./receivables/emails/EmailReplyModal";
import {
  useMarkThreadEmailsReadMutation,
  usePaymentCorrespondenceQuery,
} from "../api/queries";
import {
  EmailConversationMessages,
  sortEmailsChronologically,
} from "./emails/EmailConversationMessages";
import { isOutgoingEmail } from "./emails/emailDirection";
import { useMailboxContext } from "./emails/useMailboxContext";
import { useAuth } from "../hooks/useAuth";

interface CorrespondenceSectionProps {
  invoiceId: string;
}

type ReplyTarget = {
  message: Email;
  mode: ReplyMode;
  threadId: string;
  threadSubject: string;
};

function CorrespondenceThreadCard({
  thread,
  isOpen,
  onToggle,
  mailboxContext,
  highlightMessageId,
  onReply,
}: {
  thread: EmailThread;
  isOpen: boolean;
  onToggle: () => void;
  mailboxContext: ReplySelfContext;
  highlightMessageId?: string | null;
  onReply?: (message: Email, mode: ReplyMode) => void;
}) {
  const sortedEmails = useMemo(
    () => sortEmailsChronologically(thread.emails),
    [thread.emails],
  );
  const hasUnread = thread.emails.some((email) => !email.is_read);
  const latest = sortedEmails[sortedEmails.length - 1];
  const latestIsOutgoing = latest
    ? isOutgoingEmail(latest, mailboxContext)
    : false;

  return (
    <div
      className={`rounded-xl border shadow-sm ${
        hasUnread
          ? "border-slate-300 bg-slate-50"
          : "border-slate-200 bg-white"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-slate-900">
            {latest?.subject || "(No subject)"}
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {latestIsOutgoing ? "Sent to" : "From"}{" "}
            {latest
              ? latestIsOutgoing
                ? Array.isArray(latest.to_email)
                  ? latest.to_email.join(", ")
                  : latest.to_email
                : latest.from_email
              : "Unknown"}
            {latest?.when ? ` · ${latest.when}` : ""}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {thread.emails.length} message
            {thread.emails.length === 1 ? "" : "s"}
            {hasUnread ? " · Unread" : ""}
          </p>
        </div>
        {isOpen ? (
          <IconChevronUp size={20} className="mt-1 shrink-0 text-teal-700" />
        ) : (
          <IconChevronDown size={20} className="mt-1 shrink-0 text-slate-400" />
        )}
      </button>

      {isOpen ? (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-4">
          <EmailConversationMessages
            emails={thread.emails}
            mailboxContext={mailboxContext}
            highlightMessageId={highlightMessageId}
            onReply={onReply}
          />
        </div>
      ) : null}
    </div>
  );
}

export const CorrespondenceSection = ({
  invoiceId,
}: CorrespondenceSectionProps) => {
  const { canMutateOrg } = useAuth();
  const [searchParams] = useSearchParams();
  const deepLinkThreadId = searchParams.get("thread");
  const deepLinkMessageId = searchParams.get("message");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [replyTarget, setReplyTarget] = useState<ReplyTarget | null>(null);
  const mailboxContext = useMailboxContext();
  const { mutate: markThreadEmailsRead } = useMarkThreadEmailsReadMutation();

  const { data: correspondence = [], isLoading: isCorrespondenceLoading } =
    usePaymentCorrespondenceQuery(invoiceId, { enabled: Boolean(invoiceId) });

  const threads = useMemo(
    () => [...correspondence].reverse(),
    [correspondence],
  );

  useEffect(() => {
    if (!deepLinkThreadId || threads.length === 0) {
      return;
    }

    const threadIndex = threads.findIndex(
      (thread) => thread.thread_id === deepLinkThreadId,
    );
    if (threadIndex >= 0) {
      setOpenIndex(threadIndex);
    }
  }, [deepLinkThreadId, threads]);

  const handleReply = (
    thread: EmailThread,
    message: Email,
    mode: ReplyMode,
  ) => {
    const sortedEmails = sortEmailsChronologically(thread.emails);
    const threadSubject =
      sortedEmails[sortedEmails.length - 1]?.subject ??
      sortedEmails[0]?.subject ??
      message.subject ??
      "";

    setReplyTarget({
      message,
      mode,
      threadId: thread.thread_id,
      threadSubject,
    });
  };

  const toggleThread = (
    index: number,
    threadId: string,
    hasUnread: boolean,
  ) => {
    const isCurrentlyOpen = openIndex === index;
    setOpenIndex(isCurrentlyOpen ? null : index);

    if (!isCurrentlyOpen && hasUnread && invoiceId) {
      markThreadEmailsRead({
        externalId: invoiceId,
        gmailThreadId: threadId,
      });
    }
  };

  return (
    <div className="w-full space-y-3 pb-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Email correspondence
      </h2>

      <div className="space-y-3">
        {isCorrespondenceLoading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <p className="text-sm text-slate-500">Loading correspondence…</p>
          </div>
        ) : threads.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm">
            <p className="text-sm italic text-slate-500">
              No email correspondence available.
            </p>
          </div>
        ) : (
          threads.map((thread, index) => {
            const hasUnread = thread.emails.some((email) => !email.is_read);
            const isDeepLinkedThread =
              deepLinkThreadId != null && thread.thread_id === deepLinkThreadId;
            return (
              <CorrespondenceThreadCard
                key={`thread-${thread.thread_id}-${index}`}
                thread={thread}
                isOpen={openIndex === index}
                mailboxContext={mailboxContext}
                highlightMessageId={
                  isDeepLinkedThread ? deepLinkMessageId : null
                }
                onReply={
                  canMutateOrg
                    ? (message, mode) => handleReply(thread, message, mode)
                    : undefined
                }
                onToggle={() =>
                  toggleThread(index, thread.thread_id, hasUnread)
                }
              />
            );
          })
        )}
      </div>

      {replyTarget ? (
        <EmailReplyModal
          opened
          onClose={() => setReplyTarget(null)}
          gmailThreadId={replyTarget.threadId}
          replyMode={replyTarget.mode}
          message={replyTarget.message}
          threadSubject={replyTarget.threadSubject}
          linkedInvoiceIds={[invoiceId]}
        />
      ) : null}
    </div>
  );
};
